import { analysisGenerationClient } from "./analysisGenerationClient.ts";
import { dailyAnalysisClient } from "./dailyAnalysisClient.ts";
import { DAILY_ANALYSIS_ENABLED } from "./dailyAnalysisConfig.ts";
import type { IndicatorSeries } from "../types";
import type { HousingRtmsScopeId } from "./adapters/housingRtms";

export type HousingRtmsChartScopeId = HousingRtmsScopeId;
export type HousingOfficialChartScopeId = "national" | "noncapital";
export type HousingChartScopeId = HousingRtmsChartScopeId | HousingOfficialChartScopeId;
export type HousingChartResolution = "1d" | "3d" | "7d" | "14d" | "1m";
export type HousingChartResolutionSelection = "auto" | "tick" | HousingChartResolution;
export type HousingChartPriceBasis = "auto" | "fixed_cell" | "median_proxy" | "raw_median" | "official_index";
export type HousingChartRange = "1M" | "3M" | "6M" | "1Y" | "MAX";
export type HousingMacdPreset = "fast" | "standard";

export interface HousingChartBand {
  id: string;
  label: string;
}

export interface HousingMarketBar {
  startDate: string;
  endDate: string;
  periodEndDate?: string;
  lastObservedDate?: string;
  marketIndexValidDayCount?: number;
  transactionCount: number;
  medianPriceProxyKrw: number | null;
  p10PriceKrw: number | null;
  p90PriceKrw: number | null;
  openPriceProxyKrw: number | null;
  closePriceProxyKrw: number | null;
  lowPriceProxyKrw: number | null;
  highPriceProxyKrw: number | null;
  marketIndexOpen: number | null;
  marketIndexHigh: number | null;
  marketIndexLow: number | null;
  marketIndexClose: number | null;
  marketIndexStatus: "ready" | "insufficient";
  marketIndexSegmentId?: string | null;
  /** @deprecated schema<=1.3 name; value is normalized cell/day observation count, not literal transactions. */
  marketIndexNormalizedTransactionCount: number;
  marketIndexNormalizedCellDayCount: number;
  sampleStatus: "ready" | "low_sample" | "insufficient";
  provisional: boolean;
  fixedCellLogPriceChangePct: number | null;
  fixedCellIndex: number | null;
  matchedCellCount: number;
  matchedPreviousTransactionCount: number;
  matchedCurrentTransactionCount: number;
  /** @deprecated v1.5+: use originPreviousTransactionCoveragePct. */
  previousTransactionCoveragePct: number;
  /** @deprecated v1.5+: this compatibility field carries origin cell survival, not literal current tx coverage. */
  currentTransactionCoveragePct: number;
  originPreviousTransactionCoveragePct?: number;
  fixedCellStatus: "ready" | "insufficient";
  fixedCellIndexOpen?: number | null;
  fixedCellIndexHigh?: number | null;
  fixedCellIndexLow?: number | null;
  fixedCellSegmentId?: string | null;
  originCellCount?: number;
  originCellSurvivalPct?: number;
  fixedCellPositiveBreadthPct?: number | null;
  bandMigrationCellPct?: number;
  originCellsNearCutoffPct?: number;
  cutoffBufferPct?: number;
  observedMarketLogPriceChangePct?: number | null;
  compositionGapPct?: number | null;
  regionEqualWeightLogPriceChangePct?: number | null;
  regionMixGapPct?: number | null;
  reportingAgeDays?: number;
  initialReportingMature?: boolean;
  revisionRisk?: "initial_window_open" | "initial_window_elapsed" | string;
  methodAsReleasedPitSafe?: boolean;
  calculationLookaheadFree?: boolean;
  qualitySensitivity?: Record<string, { status?: string; logPriceChangePct?: number | null; matchedCellCount?: number }>;
  policyProxyCohorts?: Record<string, Record<string, unknown>>;
}

export interface HousingMarketTick {
  date: string;
  sigungu: string;
  legalDong: string;
  complexName: string;
  exclusiveAreaM2: number;
  floor: number | null;
  priceKrw: number;
  bandId: string;
}

export interface HousingMarketChartScopeData {
  analysisGenerationId?: string;
  sourceTicksStatus?: "not_in_analysis_cache";
  schemaVersion: string;
  generatedAt: string;
  derivedFromRtmsGeneratedAt: string | null;
  scope: HousingChartScopeId;
  scopeLabel: string;
  dataKind?: "rtms_derived" | "official_index";
  coverage: {
    startMonth: string | null;
    endMonth: string | null;
    completeMonths: string[];
    availableMonthSegments?: Array<{ startMonth: string; endMonth: string; monthCount: number }>;
    availableCompleteMonthCount?: number;
    tradeCount: number;
    currentMonthProvisional: boolean;
  };
  bands: HousingChartBand[];
  resolutions: HousingChartResolution[];
  fixedCellGuard: {
    cellDefinition: string;
    minimumMatchedCells: number;
    minimumMatchedTransactionsEachBar: number;
    minimumTransactionCoveragePctEachBar: number;
    minimumOriginPreviousTransactionCoveragePct?: number;
    minimumOriginCellSurvivalPct?: number;
    note: string;
  };
  bars: Record<HousingChartResolution, Record<string, HousingMarketBar[]>>;
  ticksByBand: Record<string, HousingMarketTick[]>;
}

export interface HousingTechnicalPoint extends HousingMarketBar {
  date: string;
  price: number | null;
  representativePriceEok: number | null;
  priceMa1: number | null;
  priceMa2: number | null;
  priceMa3: number | null;
  priceMa4: number | null;
  volume: number;
  volumeMa1: number | null;
  volumeMa2: number | null;
  volumeMa3: number | null;
  rsi: number | null;
  macd: number | null;
  macdSignal: number | null;
  macdOsc: number | null;
  qualityAdjustedComparisonIndex: number | null;
  rawObservedComparisonIndex: number | null;
  candleOpen: number | null;
  candleClose: number | null;
  candleLow: number | null;
  candleHigh: number | null;
  candleRange: [number, number] | null;
  candleChangePct: number | null;
  closeChangePct: number | null;
  candleDirection: "up" | "down" | "flat" | "na";
}

export interface HousingTechnicalConfig {
  priceMaPeriods: number[];
  volumeMaPeriods: number[];
  rsiPeriod: number;
  macdFast: number;
  macdSlow: number;
  macdSignal: number;
}

const RESOLUTION_CONFIG: Record<HousingChartResolution, Omit<HousingTechnicalConfig, "macdFast" | "macdSlow" | "macdSignal">> = {
  "1d": { priceMaPeriods: [7, 30, 90], volumeMaPeriods: [7, 30], rsiPeriod: 14 },
  "3d": { priceMaPeriods: [4, 10, 30], volumeMaPeriods: [4, 10], rsiPeriod: 14 },
  "7d": { priceMaPeriods: [4, 13, 26, 52], volumeMaPeriods: [4, 13, 26], rsiPeriod: 14 },
  "14d": { priceMaPeriods: [2, 6, 13, 26], volumeMaPeriods: [2, 6, 13], rsiPeriod: 14 },
  "1m": { priceMaPeriods: [3, 6, 12], volumeMaPeriods: [3, 6, 12], rsiPeriod: 14 }
};

function finite(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function validateBar(raw: unknown): HousingMarketBar | null {
  if (!raw || typeof raw !== "object") return null;
  const x = raw as Record<string, unknown>;
  if (typeof x.startDate !== "string" || typeof x.endDate !== "string") return null;
  const transactionCount = Number(x.transactionCount);
  if (!Number.isInteger(transactionCount) || transactionCount < 0) return null;
  const nullable = (key: string) => x[key] == null ? null : Number(x[key]);
  const medianPriceProxyKrw = nullable("medianPriceProxyKrw");
  const fixedCellIndex = nullable("fixedCellIndex");
  if (medianPriceProxyKrw != null && !finite(medianPriceProxyKrw)) return null;
  if (fixedCellIndex != null && !finite(fixedCellIndex)) return null;
  return {
    startDate: x.startDate,
    endDate: x.endDate,
    periodEndDate: typeof x.periodEndDate === "string" ? x.periodEndDate : undefined,
    lastObservedDate: typeof x.lastObservedDate === "string" ? x.lastObservedDate : undefined,
    marketIndexValidDayCount: Number(x.marketIndexValidDayCount ?? 0),
    transactionCount,
    medianPriceProxyKrw,
    p10PriceKrw: nullable("p10PriceKrw"),
    p90PriceKrw: nullable("p90PriceKrw"),
    openPriceProxyKrw: nullable("openPriceProxyKrw"),
    closePriceProxyKrw: nullable("closePriceProxyKrw"),
    lowPriceProxyKrw: nullable("lowPriceProxyKrw"),
    highPriceProxyKrw: nullable("highPriceProxyKrw"),
    marketIndexOpen: nullable("marketIndexOpen"),
    marketIndexHigh: nullable("marketIndexHigh"),
    marketIndexLow: nullable("marketIndexLow"),
    marketIndexClose: nullable("marketIndexClose"),
    marketIndexStatus: String(x.marketIndexStatus) === "ready" ? "ready" : "insufficient",
    marketIndexNormalizedTransactionCount: Number(x.marketIndexNormalizedTransactionCount ?? x.marketIndexNormalizedCellDayCount ?? 0),
    marketIndexNormalizedCellDayCount: Number(x.marketIndexNormalizedCellDayCount ?? x.marketIndexNormalizedTransactionCount ?? 0),
    sampleStatus: ["ready", "low_sample", "insufficient"].includes(String(x.sampleStatus)) ? x.sampleStatus as HousingMarketBar["sampleStatus"] : "insufficient",
    provisional: Boolean(x.provisional),
    fixedCellLogPriceChangePct: nullable("fixedCellLogPriceChangePct"),
    fixedCellIndex,
    matchedCellCount: Number(x.matchedCellCount ?? 0),
    matchedPreviousTransactionCount: Number(x.matchedPreviousTransactionCount ?? 0),
    matchedCurrentTransactionCount: Number(x.matchedCurrentTransactionCount ?? 0),
    previousTransactionCoveragePct: Number(x.previousTransactionCoveragePct ?? x.originPreviousTransactionCoveragePct ?? 0),
    currentTransactionCoveragePct: Number(x.currentTransactionCoveragePct ?? x.originCellSurvivalPct ?? 0),
    originPreviousTransactionCoveragePct: Number(x.originPreviousTransactionCoveragePct ?? x.previousTransactionCoveragePct ?? 0),
    fixedCellStatus: String(x.fixedCellStatus) === "ready" ? "ready" : "insufficient",
    fixedCellIndexOpen: nullable("fixedCellIndexOpen"),
    fixedCellIndexHigh: nullable("fixedCellIndexHigh"),
    fixedCellIndexLow: nullable("fixedCellIndexLow"),
    fixedCellSegmentId: typeof x.fixedCellSegmentId === "string" ? x.fixedCellSegmentId : null,
    originCellCount: Number(x.originCellCount ?? 0),
    originCellSurvivalPct: Number(x.originCellSurvivalPct ?? 0),
    fixedCellPositiveBreadthPct: nullable("fixedCellPositiveBreadthPct"),
    bandMigrationCellPct: Number(x.bandMigrationCellPct ?? 0),
    originCellsNearCutoffPct: Number(x.originCellsNearCutoffPct ?? 0),
    cutoffBufferPct: Number(x.cutoffBufferPct ?? 0),
    observedMarketLogPriceChangePct: nullable("observedMarketLogPriceChangePct"),
    compositionGapPct: nullable("compositionGapPct"),
    regionEqualWeightLogPriceChangePct: nullable("regionEqualWeightLogPriceChangePct"),
    regionMixGapPct: nullable("regionMixGapPct"),
    reportingAgeDays: Number(x.reportingAgeDays ?? 0),
    initialReportingMature: Boolean(x.initialReportingMature),
    revisionRisk: typeof x.revisionRisk === "string" ? x.revisionRisk : undefined,
    methodAsReleasedPitSafe: Boolean(x.methodAsReleasedPitSafe),
    calculationLookaheadFree: x.calculationLookaheadFree !== false,
    qualitySensitivity: x.qualitySensitivity && typeof x.qualitySensitivity === "object" ? x.qualitySensitivity as HousingMarketBar["qualitySensitivity"] : undefined,
    policyProxyCohorts: x.policyProxyCohorts && typeof x.policyProxyCohorts === "object" ? x.policyProxyCohorts as HousingMarketBar["policyProxyCohorts"] : undefined
  };
}

export async function loadHousingMarketChartScope(scope: HousingRtmsChartScopeId, signal?: AbortSignal): Promise<HousingMarketChartScopeData> {
  if (DAILY_ANALYSIS_ENABLED) return dailyAnalysisClient.read<HousingMarketChartScopeData>(`chart-${scope}`, signal);
  const selected = await analysisGenerationClient.read(`chart-${scope}`, signal);
  let raw: Record<string, unknown>;
  if (selected.mode === "generation") raw = selected.data;
  else {
    const path = `public/data/housing/chart/${scope}.json`;
    const response = await fetch(`/api/shared-import?path=${encodeURIComponent(path)}`, { signal, cache: "no-store" });
    if (!response.ok) throw new Error(`Housing technical chart ${scope}: HTTP ${response.status}`);
    raw = await response.json() as Record<string, unknown>;
  }
  if (!raw || !["1.0", "1.1", "1.2", "1.3", "1.4", "1.5"].includes(String(raw.schemaVersion)) || raw.scope !== scope) throw new Error(`Housing technical chart ${scope}: invalid schema`);
  const rawBars = (raw.bars ?? {}) as Record<string, Record<string, unknown[]>>;
  const resolutions: HousingChartResolution[] = ["1d", "3d", "7d", "14d", "1m"];
  const bars = Object.fromEntries(resolutions.map((resolution) => [
    resolution,
    Object.fromEntries(Object.entries(rawBars[resolution] ?? {}).map(([band, rows]) => [band, (rows ?? []).map(validateBar).filter(Boolean) as HousingMarketBar[]]))
  ])) as HousingMarketChartScopeData["bars"];
  return { ...(raw as unknown as HousingMarketChartScopeData), bars,
    ...(selected.mode === "generation" ? { analysisGenerationId: selected.generationId, sourceTicksStatus: "not_in_analysis_cache" as const, ticksByBand: {} } : {}) };
}

export function isOfficialHousingChartScope(scope: HousingChartScopeId): scope is HousingOfficialChartScopeId {
  return scope === "national" || scope === "noncapital";
}

function monthEnd(dateText: string) {
  const d = new Date(`${dateText.slice(0, 7)}-01T00:00:00Z`);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).toISOString().slice(0, 10);
}

export function buildOfficialHousingChartScopeData(
  scope: HousingOfficialChartScopeId,
  officialHousingPrices: IndicatorSeries[]
): HousingMarketChartScopeData | null {
  const seriesId = scope === "national" ? "housing_price_national_apartment" : "housing_price_noncapital_apartment";
  const label = scope === "national" ? "전국" : "비수도권";
  const source = officialHousingPrices.find((series) => series.id === seriesId);
  if (!source) return null;
  const observations = source.observations
    .filter((row) => finite(row.value))
    .map((row) => ({ date: row.date, value: row.value as number }))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (!observations.length) return null;
  const base = observations[0].value;
  if (!finite(base) || base === 0) return null;
  let previousClose: number | null = null;
  const monthlyBars: HousingMarketBar[] = observations.map((row) => {
    const close = 100 * row.value / base;
    const open = previousClose ?? close;
    const high = Math.max(open, close);
    const low = Math.min(open, close);
    previousClose = close;
    return {
      startDate: `${row.date.slice(0, 7)}-01`,
      endDate: monthEnd(row.date),
      periodEndDate: monthEnd(row.date),
      lastObservedDate: monthEnd(row.date),
      marketIndexValidDayCount: 1,
      transactionCount: 0,
      medianPriceProxyKrw: null,
      p10PriceKrw: null,
      p90PriceKrw: null,
      openPriceProxyKrw: null,
      closePriceProxyKrw: null,
      lowPriceProxyKrw: null,
      highPriceProxyKrw: null,
      marketIndexOpen: open,
      marketIndexHigh: high,
      marketIndexLow: low,
      marketIndexClose: close,
      marketIndexStatus: "ready",
      marketIndexNormalizedTransactionCount: 0,
      marketIndexNormalizedCellDayCount: 0,
      sampleStatus: "ready",
      provisional: false,
      fixedCellLogPriceChangePct: null,
      fixedCellIndex: null,
      matchedCellCount: 0,
      matchedPreviousTransactionCount: 0,
      matchedCurrentTransactionCount: 0,
      previousTransactionCoveragePct: 0,
      currentTransactionCoveragePct: 0,
      fixedCellStatus: "insufficient"
    };
  });
  const empty = { all: [] as HousingMarketBar[] };
  const bars: HousingMarketChartScopeData["bars"] = {
    "1d": empty, "3d": empty, "7d": empty, "14d": empty, "1m": { all: monthlyBars }
  };
  return {
    schemaVersion: "official-1.0",
    generatedAt: source.source.collectedAt ?? new Date().toISOString(),
    derivedFromRtmsGeneratedAt: null,
    scope,
    scopeLabel: label,
    dataKind: "official_index",
    coverage: {
      startMonth: observations[0].date.slice(0, 7),
      endMonth: observations.at(-1)?.date.slice(0, 7) ?? null,
      completeMonths: observations.map((row) => row.date.slice(0, 7)),
      tradeCount: 0,
      currentMonthProvisional: false
    },
    bands: [{ id: "all", label: "전체" }],
    resolutions: ["1m"],
    fixedCellGuard: {
      cellDefinition: "N/A — 한국부동산원 공식 월간 아파트 매매가격지수",
      minimumMatchedCells: 0,
      minimumMatchedTransactionsEachBar: 0,
      minimumTransactionCoveragePctEachBar: 0,
      note: "전국/비수도권은 현재 수도권 RTMS 파생차트가 아니라 ECOS/한국부동산원 공식 월지수로 표시합니다."
    },
    bars,
    ticksByBand: { all: [] }
  };
}

function medianNumber(values: number[]) {
  if (!values.length) return null;
  const xs = [...values].sort((a, b) => a - b);
  const i = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[i] : (xs[i - 1] + xs[i]) / 2;
}

function readyMarketBar(bar: HousingMarketBar) {
  return bar.marketIndexStatus === "ready"
    && finite(bar.marketIndexOpen)
    && finite(bar.marketIndexHigh)
    && finite(bar.marketIndexLow)
    && finite(bar.marketIndexClose);
}

export function canonicalHousingChartBars(
  data: HousingMarketChartScopeData,
  resolution: HousingChartResolution,
  bandId: string
): HousingMarketBar[] {
  const target = data.bars[resolution]?.[bandId] ?? [];
  if (resolution === "1d" || data.dataKind === "official_index") return target;
  const daily = (data.bars["1d"]?.[bandId] ?? []).filter(readyMarketBar);
  if (!daily.length) return target;

  // Rebuild every higher timeframe from the canonical valid daily candles. This
  // also repairs older schema 1.2 runtime files without waiting for the next
  // RTMS Action. Fixed-cell/raw metadata remain attached to their original
  // aggregate bucket; only synthetic market OHLC/date geometry is replaced.
  return target.map((source) => {
    const periodEnd = source.periodEndDate ?? source.endDate;
    const members = daily.filter((bar) => bar.endDate >= source.startDate && bar.endDate <= periodEnd);
    if (!members.length) {
      return {
        ...source,
        periodEndDate: periodEnd,
        marketIndexOpen: null,
        marketIndexHigh: null,
        marketIndexLow: null,
        marketIndexClose: null,
        marketIndexStatus: "insufficient",
        marketIndexNormalizedTransactionCount: 0,
        marketIndexNormalizedCellDayCount: 0,
        marketIndexValidDayCount: 0
      };
    }
    const first = members[0];
    const last = members[members.length - 1];
    return {
      ...source,
      periodEndDate: periodEnd,
      endDate: last.endDate,
      marketIndexOpen: first.marketIndexOpen,
      marketIndexHigh: Math.max(...members.map((bar) => bar.marketIndexHigh as number)),
      marketIndexLow: Math.min(...members.map((bar) => bar.marketIndexLow as number)),
      marketIndexClose: last.marketIndexClose,
      marketIndexStatus: "ready",
      marketIndexNormalizedTransactionCount: members.reduce((sum, bar) => sum + (bar.marketIndexNormalizedCellDayCount || bar.marketIndexNormalizedTransactionCount || 0), 0),
      marketIndexNormalizedCellDayCount: members.reduce((sum, bar) => sum + (bar.marketIndexNormalizedCellDayCount || bar.marketIndexNormalizedTransactionCount || 0), 0),
      marketIndexValidDayCount: members.length
    };
  });
}

export interface HousingResolutionQuality {
  resolution: HousingChartResolution;
  recentBarCount: number;
  observedReadyShare: number;
  rawReadyShare: number;
  sampleReadyShare: number;
  medianTransactionCount: number;
  medianValidDayShare: number;
  fixedReadyShare: number;
  fixedMatureReadyShare: number;
  fixedSegmentCount: number;
  fixedMatureSegmentCount: number;
  fixedLatestReady: boolean;
  fixedCurrentRun: number;
  observedUsable: boolean;
  rawUsable: boolean;
  fixedUsable: boolean;
  observedScore: number;
  rawScore: number;
  fixedScore: number;
}

function calendarDayCount(bar: HousingMarketBar) {
  const start = Date.parse(`${bar.startDate}T00:00:00Z`);
  const end = Date.parse(`${bar.periodEndDate ?? bar.endDate}T00:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) return 1;
  return Math.max(1, Math.round((end - start) / 86_400_000) + 1);
}

export function assessHousingResolutionQuality(resolution: HousingChartResolution, bars: HousingMarketBar[]): HousingResolutionQuality {
  const recent = bars.filter((bar) => !bar.provisional).slice(-12);
  const observedReady = recent.filter(readyMarketBar);
  const rawReady = recent.filter((bar) => finite(bar.medianPriceProxyKrw) && (bar.medianPriceProxyKrw as number) > 0 && bar.transactionCount > 0);
  const fixedReady = recent.filter((bar) => bar.fixedCellStatus === "ready" && finite(bar.fixedCellIndex) && Boolean(bar.fixedCellSegmentId));
  const fixedMatureReady = fixedReady.filter((bar) => bar.initialReportingMature !== false);
  const observedReadyShare = recent.length ? observedReady.length / recent.length : 0;
  const rawReadyShare = recent.length ? rawReady.length / recent.length : 0;
  const sampleReadyShare = recent.length ? recent.filter((bar) => bar.sampleStatus === "ready").length / recent.length : 0;
  const medianTransactionCount = medianNumber(recent.map((bar) => bar.transactionCount)) ?? 0;
  const validDayShares = observedReady.map((bar) => Math.min(1, Math.max(0, Number(bar.marketIndexValidDayCount ?? 0)) / calendarDayCount(bar)));
  const medianValidDayShare = medianNumber(validDayShares) ?? 0;
  const fixedReadyShare = recent.length ? fixedReady.length / recent.length : 0;
  const fixedMatureReadyShare = recent.length ? fixedMatureReady.length / recent.length : 0;
  const fixedSegmentCount = new Set(fixedReady.map((bar) => bar.fixedCellSegmentId)).size;
  const fixedMatureSegmentCount = new Set(fixedMatureReady.map((bar) => bar.fixedCellSegmentId)).size;
  const latest = recent.at(-1);
  const fixedLatestReady = Boolean(latest && latest.fixedCellStatus === "ready" && finite(latest.fixedCellIndex) && latest.fixedCellSegmentId);
  let fixedCurrentRun = 0;
  if (fixedLatestReady) {
    const segment = latest?.fixedCellSegmentId ?? null;
    for (let i = recent.length - 1; i >= 0; i -= 1) {
      const bar = recent[i];
      if (bar.fixedCellStatus !== "ready" || !finite(bar.fixedCellIndex) || bar.fixedCellSegmentId !== segment) break;
      fixedCurrentRun += 1;
    }
  }
  const enoughHistory = recent.length >= 8;
  const observedUsable = enoughHistory && observedReadyShare >= 0.75 && sampleReadyShare >= 0.70 && medianTransactionCount >= 10;
  const rawUsable = enoughHistory && rawReadyShare >= 0.75 && sampleReadyShare >= 0.70 && medianTransactionCount >= 10;
  // AUTO selection must not treat bars still inside the initial RTMS reporting
  // window as mature evidence. They remain displayable, but at least 75% of
  // the recent window must also be mature Fixed-cell observations.
  const fixedUsable = resolution !== "1d" && resolution !== "3d" && enoughHistory
    && fixedReadyShare >= 0.75
    && fixedMatureReadyShare >= 0.75
    && fixedMatureSegmentCount <= 2
    && fixedLatestReady
    && fixedCurrentRun >= 3;
  const observedScore = observedReadyShare * 45 + sampleReadyShare * 20 + Math.min(1, medianTransactionCount / 100) * 15 + medianValidDayShare * 20;
  const rawScore = rawReadyShare * 55 + sampleReadyShare * 25 + Math.min(1, medianTransactionCount / 100) * 20;
  const fixedScore = fixedReady.length
    ? fixedReadyShare * 40 + fixedMatureReadyShare * 20 + (fixedLatestReady ? 20 : 0) + Math.min(1, fixedCurrentRun / 6) * 15 + Math.max(0, 5 - Math.max(0, fixedMatureSegmentCount - 1) * 2)
    : 0;
  return {
    resolution, recentBarCount: recent.length, observedReadyShare, rawReadyShare, sampleReadyShare, medianTransactionCount, medianValidDayShare,
    fixedReadyShare, fixedMatureReadyShare, fixedSegmentCount, fixedMatureSegmentCount, fixedLatestReady, fixedCurrentRun, observedUsable, rawUsable, fixedUsable, observedScore, rawScore, fixedScore
  };
}

function bestFallbackResolution(assessed: HousingResolutionQuality[], score: (quality: HousingResolutionQuality) => number) {
  const coarserRank: Record<HousingChartResolution, number> = { "1d": 0, "3d": 1, "7d": 2, "14d": 3, "1m": 4 };
  return [...assessed].sort((a, b) => score(b) - score(a) || coarserRank[b.resolution] - coarserRank[a.resolution])[0]?.resolution ?? "1m";
}

export function chooseRecommendedResolution(
  scope: HousingChartScopeId,
  bandId: string,
  data: HousingMarketChartScopeData,
  basis: HousingChartPriceBasis = "auto"
): HousingChartResolution | null {
  if (isOfficialHousingChartScope(scope) || data.dataKind === "official_index") return "1m";
  const candidates: HousingChartResolution[] = ["7d", "14d", "1m"];
  const assessed = candidates.map((resolution) => assessHousingResolutionQuality(resolution, data.bars[resolution]?.[bandId] ?? []));
  if (basis === "fixed_cell") return assessed.find((quality) => quality.fixedUsable)?.resolution ?? null;
  if (basis === "raw_median") return assessed.find((quality) => quality.rawUsable)?.resolution ?? null;
  return assessed.find((quality) => quality.observedUsable)?.resolution ?? null;
}

export function chooseAutoResolution(
  scope: HousingChartScopeId,
  bandId: string,
  data: HousingMarketChartScopeData,
  basis: HousingChartPriceBasis = "auto"
): HousingChartResolution {
  if (isOfficialHousingChartScope(scope) || data.dataKind === "official_index") return "1m";
  const candidates: HousingChartResolution[] = ["7d", "14d", "1m"];
  const assessed = candidates.map((resolution) => assessHousingResolutionQuality(resolution, data.bars[resolution]?.[bandId] ?? []));
  if (basis === "fixed_cell") {
    const fixed = assessed.find((quality) => quality.fixedUsable);
    if (fixed) return fixed.resolution;
    return bestFallbackResolution(assessed, (quality) => quality.fixedScore);
  }
  if (basis === "raw_median") {
    const raw = assessed.find((quality) => quality.rawUsable);
    if (raw) return raw.resolution;
    return bestFallbackResolution(assessed, (quality) => quality.rawScore);
  }

  // AUTO is resolution-first and deliberately uses the continuous cell-normalized
  // market series as the primary HTS view. Strict Fixed remains a diagnostic
  // comparison rather than silently replacing the main line merely because a
  // common-cell run happens to be available at the selected resolution.
  const observed = assessed.find((quality) => quality.observedUsable);
  if (observed) return observed.resolution;
  return bestFallbackResolution(assessed, (quality) => quality.observedScore);
}

export function chooseAutoPriceBasis(_resolution: HousingChartResolution, _bars: HousingMarketBar[]): Exclude<HousingChartPriceBasis, "auto"> {
  // The cell-normalized series is the default market view. Strict Fixed is kept
  // explicit because its adjacent-bar exact-cell requirement is intentionally
  // sparse and should never take over the primary HTS line automatically.
  return "median_proxy";
}

function sma(values: Array<number | null>, period: number): Array<number | null> {
  const out = new Array<number | null>(values.length).fill(null);
  const window: number[] = [];
  let sum = 0;
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (!finite(value)) continue;
    window.push(value);
    sum += value;
    if (window.length > period) sum -= window.shift()!;
    if (window.length === period) out[i] = sum / period;
  }
  return out;
}

function ema(values: Array<number | null>, period: number): Array<number | null> {
  const out = new Array<number | null>(values.length).fill(null);
  const alpha = 2 / (period + 1);
  let current: number | null = null;
  let seen = 0;
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (!finite(value)) continue;
    current = current == null ? value : alpha * value + (1 - alpha) * current;
    seen += 1;
    if (seen >= period) out[i] = current;
  }
  return out;
}

function rsi(values: Array<number | null>, period: number): Array<number | null> {
  const out = new Array<number | null>(values.length).fill(null);
  let avgGain: number | null = null;
  let avgLoss: number | null = null;
  const gains: number[] = [];
  const losses: number[] = [];
  let previous: number | null = null;
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (!finite(value)) continue;
    if (previous == null) { previous = value; continue; }
    const delta = value - previous;
    previous = value;
    const gain = Math.max(delta, 0);
    const loss = Math.max(-delta, 0);
    if (avgGain == null || avgLoss == null) {
      gains.push(gain); losses.push(loss);
      if (gains.length < period) continue;
      avgGain = gains.slice(-period).reduce((a, b) => a + b, 0) / period;
      avgLoss = losses.slice(-period).reduce((a, b) => a + b, 0) / period;
    } else {
      avgGain = (avgGain * (period - 1) + gain) / period;
      avgLoss = (avgLoss * (period - 1) + loss) / period;
    }
    out[i] = avgLoss === 0 ? (avgGain === 0 ? 50 : 100) : 100 - (100 / (1 + avgGain / avgLoss));
  }
  return out;
}

export function technicalConfig(resolution: HousingChartResolution, preset: HousingMacdPreset): HousingTechnicalConfig {
  const base = RESOLUTION_CONFIG[resolution];
  return {
    ...base,
    macdFast: preset === "fast" ? 6 : 12,
    macdSlow: preset === "fast" ? 12 : 26,
    macdSignal: preset === "fast" ? 4 : 9
  };
}

function rawMedianBase(bars: HousingMarketBar[]): number | null {
  const first = bars.find((bar) => finite(bar.medianPriceProxyKrw) && (bar.medianPriceProxyKrw as number) > 0)?.medianPriceProxyKrw;
  return finite(first) && first > 0 ? first : null;
}

function rawMedianIndexSeries(bars: HousingMarketBar[]): Array<number | null> {
  const base = rawMedianBase(bars);
  if (!finite(base) || base <= 0) return bars.map(() => null);
  return bars.map((bar) => finite(bar.medianPriceProxyKrw) && (bar.medianPriceProxyKrw as number) > 0
    ? 100 * (bar.medianPriceProxyKrw as number) / base
    : null);
}

function qualityAdjustedIndexSeries(bars: HousingMarketBar[]): Array<number | null> {
  return bars.map((bar) => bar.marketIndexStatus === "ready" && finite(bar.marketIndexClose) ? bar.marketIndexClose : null);
}

function chainedIndexSeries(
  bars: HousingMarketBar[],
  basis: Exclude<HousingChartPriceBasis, "auto">
): Array<number | null> {
  if (basis === "fixed_cell") {
    return bars.map((bar) => bar.fixedCellStatus === "ready" && finite(bar.fixedCellIndex) ? bar.fixedCellIndex as number : null);
  }
  if (basis === "raw_median") return rawMedianIndexSeries(bars);
  return qualityAdjustedIndexSeries(bars);
}

function normalizedFixedCellOverlay(bars: HousingMarketBar[], closes: Array<number | null>): Array<number | null> {
  const out = new Array<number | null>(bars.length).fill(null);
  let segmentId: string | null = null;
  let segmentBaseFixed: number | null = null;
  let segmentBaseClose: number | null = null;
  for (let i = 0; i < bars.length; i += 1) {
    const fixed = bars[i].fixedCellIndex;
    const close = closes[i];
    const nextSegment = bars[i].fixedCellSegmentId ?? null;
    if (finite(fixed) && finite(close) && nextSegment) {
      if (segmentId !== nextSegment || segmentBaseFixed == null || segmentBaseClose == null) {
        segmentId = nextSegment;
        segmentBaseFixed = fixed;
        segmentBaseClose = close;
      }
      out[i] = segmentBaseClose * ((fixed as number) / Math.max(segmentBaseFixed, 1e-9));
    } else {
      segmentId = null;
      segmentBaseFixed = null;
      segmentBaseClose = null;
    }
  }
  return out;
}

function normalizedSeriesOverlay(
  comparison: Array<number | null>,
  closes: Array<number | null>,
  segments?: Array<string | null>
): Array<number | null> {
  const out = new Array<number | null>(comparison.length).fill(null);
  let segment: string | null | undefined = undefined;
  let baseComparison: number | null = null;
  let baseClose: number | null = null;
  for (let i = 0; i < comparison.length; i += 1) {
    const nextSegment = segments?.[i] ?? "market";
    const candidate = comparison[i];
    const close = closes[i];
    if (!finite(candidate) || !finite(close) || nextSegment == null) {
      if (segments) { segment = undefined; baseComparison = null; baseClose = null; }
      continue;
    }
    if (segment !== nextSegment || baseComparison == null || baseClose == null) {
      segment = nextSegment;
      baseComparison = candidate;
      baseClose = close;
    }
    out[i] = baseClose * ((candidate as number) / Math.max(baseComparison, 1e-9));
  }
  return out;
}

function segmentedApply(
  values: Array<number | null>,
  segments: Array<string | null>,
  calculate: (slice: Array<number | null>) => Array<number | null>
): Array<number | null> {
  const out = new Array<number | null>(values.length).fill(null);
  let start = 0;
  while (start < values.length) {
    const segment = segments[start];
    let end = start + 1;
    while (end < values.length && segments[end] === segment) end += 1;
    const calculated = calculate(values.slice(start, end));
    for (let i = 0; i < calculated.length; i += 1) out[start + i] = calculated[i];
    start = end;
  }
  return out;
}

export function buildHousingTechnicalPoints(
  bars: HousingMarketBar[],
  resolution: HousingChartResolution,
  basis: Exclude<HousingChartPriceBasis, "auto">,
  preset: HousingMacdPreset
): { points: HousingTechnicalPoint[]; config: HousingTechnicalConfig } {
  const config = technicalConfig(resolution, preset);
  // Quality-adjusted keeps the canonical valid-price trading-bar axis. Raw median
  // keeps bars with actual transactions. Strict Fixed keeps every aggregate bucket
  // so a common-cell failure remains an honest calendar gap.
  const tradingBars = basis === "fixed_cell"
    ? bars
    : basis === "raw_median"
      ? bars.filter((bar) => finite(bar.medianPriceProxyKrw) && (bar.medianPriceProxyKrw as number) > 0 && bar.transactionCount > 0)
      : bars.filter(readyMarketBar);
  const closes = chainedIndexSeries(tradingBars, basis);
  const qualitySeries = qualityAdjustedIndexSeries(tradingBars);
  const rawSeries = rawMedianIndexSeries(tradingBars);
  const fixedOverlay = basis === "fixed_cell" ? closes : normalizedFixedCellOverlay(tradingBars, closes);
  const fixedSegments = tradingBars.map((bar) => bar.fixedCellStatus === "ready" && finite(bar.fixedCellIndex) ? bar.fixedCellSegmentId ?? null : null);
  const qualityOverlay = basis === "median_proxy" || basis === "official_index"
    ? qualitySeries
    : normalizedSeriesOverlay(qualitySeries, closes, basis === "fixed_cell" ? fixedSegments : undefined);
  const rawOverlay = basis === "raw_median"
    ? rawSeries
    : normalizedSeriesOverlay(rawSeries, closes, basis === "fixed_cell" ? fixedSegments : undefined);
  const volumes = tradingBars.map((bar) => bar.transactionCount as number | null);
  const segments = basis === "fixed_cell"
    ? tradingBars.map((bar) => bar.fixedCellStatus === "ready" && finite(bar.fixedCellIndex) ? bar.fixedCellSegmentId ?? null : null)
    : tradingBars.map((bar) => bar.marketIndexSegmentId ?? "market");
  const priceMas = config.priceMaPeriods.map((period) => segmentedApply(closes, segments, (slice) => sma(slice, period)));
  const volumeMas = config.volumeMaPeriods.map((period) => sma(volumes, period));
  const rsiValues = segmentedApply(closes, segments, (slice) => rsi(slice, config.rsiPeriod));
  const fastEma = segmentedApply(closes, segments, (slice) => ema(slice, config.macdFast));
  const slowEma = segmentedApply(closes, segments, (slice) => ema(slice, config.macdSlow));
  const macd = closes.map((_, i) => finite(fastEma[i]) && finite(slowEma[i]) ? (fastEma[i] as number) - (slowEma[i] as number) : null);
  const signal = segmentedApply(macd, segments, (slice) => ema(slice, config.macdSignal));
  const osc = macd.map((value, i) => finite(value) && finite(signal[i]) ? value - (signal[i] as number) : null);

  return {
    config,
    points: tradingBars.map((bar, i) => {
      const representativePriceEok = finite(bar.medianPriceProxyKrw) ? (bar.medianPriceProxyKrw as number) / 100_000_000 : null;
      const fixedBasis = basis === "fixed_cell";
      const rawBasis = basis === "raw_median";
      const candleClose = fixedBasis
        ? (finite(bar.fixedCellIndex) ? bar.fixedCellIndex as number : null)
        : rawBasis
          ? rawSeries[i]
          : (bar.marketIndexStatus === "ready" && finite(bar.marketIndexClose) ? bar.marketIndexClose : null);
      const rawPreviousClose = rawBasis && i > 0 && finite(rawSeries[i - 1]) ? rawSeries[i - 1] as number : null;
      const candleOpen = fixedBasis
        ? (finite(bar.fixedCellIndexOpen) ? bar.fixedCellIndexOpen as number : candleClose)
        : rawBasis
          ? (rawPreviousClose ?? candleClose)
          : (bar.marketIndexStatus === "ready" && finite(bar.marketIndexOpen) ? bar.marketIndexOpen : candleClose);
      const candleLow = fixedBasis
        ? (finite(bar.fixedCellIndexLow) ? bar.fixedCellIndexLow as number : (finite(candleOpen) && finite(candleClose) ? Math.min(candleOpen, candleClose) : null))
        : rawBasis
          ? (finite(candleOpen) && finite(candleClose) ? Math.min(candleOpen, candleClose) : null)
          : (bar.marketIndexStatus === "ready" && finite(bar.marketIndexLow) ? bar.marketIndexLow : null);
      const candleHigh = fixedBasis
        ? (finite(bar.fixedCellIndexHigh) ? bar.fixedCellIndexHigh as number : (finite(candleOpen) && finite(candleClose) ? Math.max(candleOpen, candleClose) : null))
        : rawBasis
          ? (finite(candleOpen) && finite(candleClose) ? Math.max(candleOpen, candleClose) : null)
          : (bar.marketIndexStatus === "ready" && finite(bar.marketIndexHigh) ? bar.marketIndexHigh : null);
      const candleChangePct = finite(candleOpen) && finite(candleClose) && candleOpen !== 0 ? ((candleClose as number) / (candleOpen as number) - 1) * 100 : null;
      const previousClose = i > 0 && segments[i - 1] === segments[i] && finite(closes[i - 1]) ? closes[i - 1] as number : null;
      const closeChangePct = finite(previousClose) && finite(candleClose) && previousClose !== 0 ? ((candleClose as number) / (previousClose as number) - 1) * 100 : candleChangePct;
      const candleDirection = !finite(candleOpen) || !finite(candleClose) ? "na" : candleClose > candleOpen ? "up" : candleClose < candleOpen ? "down" : "flat";
      return ({
        ...bar,
        date: bar.endDate,
        representativePriceEok,
        price: closes[i],
        priceMa1: priceMas[0]?.[i] ?? null,
        priceMa2: priceMas[1]?.[i] ?? null,
        priceMa3: priceMas[2]?.[i] ?? null,
        priceMa4: priceMas[3]?.[i] ?? null,
        volume: bar.transactionCount,
        volumeMa1: volumeMas[0]?.[i] ?? null,
        volumeMa2: volumeMas[1]?.[i] ?? null,
        volumeMa3: volumeMas[2]?.[i] ?? null,
        rsi: rsiValues[i],
        macd: macd[i],
        macdSignal: signal[i],
        macdOsc: osc[i],
        fixedCellIndex: fixedOverlay[i],
        qualityAdjustedComparisonIndex: qualityOverlay[i],
        rawObservedComparisonIndex: rawOverlay[i],
        candleOpen,
        candleClose,
        candleLow,
        candleHigh,
        candleRange: finite(candleLow) && finite(candleHigh) ? [candleLow, candleHigh] : null,
        candleChangePct,
        closeChangePct,
        candleDirection
      });
    })
  };
}

export function filterHousingChartRange<T extends { date: string }>(points: T[], range: HousingChartRange): T[] {
  if (range === "MAX" || !points.length) return points;
  const latest = new Date(`${points[points.length - 1].date}T00:00:00Z`);
  const months = range === "1M" ? 1 : range === "3M" ? 3 : range === "6M" ? 6 : 12;
  const cutoff = new Date(Date.UTC(latest.getUTCFullYear(), latest.getUTCMonth() - months, latest.getUTCDate()));
  return points.filter((point) => new Date(`${point.date}T00:00:00Z`) >= cutoff);
}
